import { useState, useEffect } from "react";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import * as APIStore from "../../store/APIStore";
import MUIDataTable from "mui-datatables";
import { Grid, Card, Typography } from "@material-ui/core";
import BarChartIcon from '@material-ui/icons/BarChart';
import moment from "moment";
import FacilityAdmitTypeBarChart from "./FacilityAdmitTypeBarChart";
import FacilityAdmissionByMonthChart from "./FacilityAdmissionByMonthChart";

type OwnProps = {
    facilityId: number;
};

const mapState = (state: ApplicationState) => state.vitalsContext;

type StateProps = ReturnType<typeof mapState>;
type DispatchProps = typeof APIStore.actionCreators;
type DrillDownFacillityProps = OwnProps & StateProps & DispatchProps;

const tableColumns = [
    {
        name: "Patient",
        options: { filter: false }
    },
    {
        name: "Admit Date",
        options: { filter: false }
    },
    {
        name: "DOB",
        options: { filter: false }
    },
    {
        name: "Primary Care Provider",
        options: { filter: false }
    }
];

const tableOptions = {
    elevation: 0,
    filter: false,
    viewColumns: false,
    selectableRows: 'none' as const,
    selectableRowsOnClick: false,
    print: false,
    download: false,
    search: false,
    setRowProps: () => ({
        style: { cursor: 'default' }
    }),
};

const DrillDownFacillity = (props: DrillDownFacillityProps) => {
    const {
        PatientLocationStats,
        Patients,
        userPracticeCoordinatorProfile,
        APIToken,
        requestCurrentUserCareCoordinatorProfile,
        requestPatientLocationStatsForProvider,
        requestPatientsByPracticeIdWithDays,
        facilityId,
    } = props;

    const [tokenState, setTokenState] = useState<APIStore.TokenType | undefined>();
    const [totalAdmits, setTotalAdmits] = useState<number>(0);
    const [facilityPatients, setFacilityPatients] = useState<APIStore.ListPatient[]>([]);

    useEffect(() => {
        if (APIToken && !tokenState?.accessToken) {
            setTokenState(APIToken);
        }
    }, [APIToken, tokenState]);

    useEffect(() => {
        if (userPracticeCoordinatorProfile) {
            let { isErrored, notFound, isLoaded } = userPracticeCoordinatorProfile
            if ((!isErrored && !notFound && !isLoaded) && tokenState?.accessToken) {
                requestCurrentUserCareCoordinatorProfile(tokenState.accessToken);

            } else {
                if (isLoaded && tokenState?.accessToken) {
                    let coordinatorProfile = userPracticeCoordinatorProfile.coordinatorProfile;

                    // Update Patient List
                    if (!Patients.isLoaded && !Patients.isLoading) {
                        requestPatientsByPracticeIdWithDays(
                            coordinatorProfile.defaultPractice.practiceId,
                            coordinatorProfile.practiceCareCoordinatorId,
                            (30 * 6), //(6 months)
                            tokenState?.accessToken
                        );
                    }
                }

            }
        }

    }, [tokenState, userPracticeCoordinatorProfile.coordinatorProfile, Patients]);

    useEffect(() => {
        // Update Location Stats
        if (tokenState?.accessToken && !PatientLocationStats.isLoaded && !PatientLocationStats.isLoading) {
            requestPatientLocationStatsForProvider(tokenState.accessToken);
        }
    }, [tokenState, PatientLocationStats]);

    useEffect(() => {
        PatientLocationStats.patientFacilityCounts.forEach((facility: APIStore.PatientFacility) => {
            if (facility.facilityId == facilityId) {
                setTotalAdmits(facility.patientsCount);
            }
        });
    }, [PatientLocationStats]);

    useEffect(() => {
        if (Patients?.patientsByPracticeId) {
            setFacilityPatients(
                Patients.patientsByPracticeId.filter((patient: APIStore.ListPatient) => patient.facilityId == facilityId)
            );
        }
    }, [Patients?.patientsByPracticeId]);

    const tableData = facilityPatients.map((patient: APIStore.ListPatient) => [
            `${patient.firstName} ${patient.lastName}`,
            patient.admitDate ? moment(patient.admitDate).format("MM-DD-YYYY") : 'N/A',
            patient.dateOfBirth ? moment(patient.dateOfBirth).format("MM-DD-YYYY") : 'N/A',
            patient.primaryCareProvider
        ]);

    const isPatientsLoading = !Patients.isLoaded || Patients.isLoading;

    return (
        <Grid container direction="column" spacing={3}>
            <Grid item xs={12}>
                <Typography variant="subtitle1">Total Admissions: {totalAdmits}</Typography>
            </Grid>

            <Grid item xs={12} container spacing={2}>
                <Grid item xs={6}>
                    <Card elevation={3} style={{ padding: 16 }}>
                        <Typography variant="subtitle2" gutterBottom>Admission by Month</Typography>
                        <FacilityAdmissionByMonthChart facilityId={facilityId} />
                    </Card>
                </Grid>
                <Grid item xs={6}>
                    <Card elevation={3} style={{ padding: 16 }}>
                        <Typography variant="subtitle2" gutterBottom>Admission Types</Typography>
                        <FacilityAdmitTypeBarChart facilityId={facilityId} />
                    </Card>
                </Grid>
            </Grid>

            <Grid item xs={12}>
                {isPatientsLoading ?
                    <BarChartIcon className="animate-flicker" style={{ fontSize: 100 }} color="disabled" />
                    :
                    <MUIDataTable
                        title=""
                        data={tableData}
                        columns={tableColumns}
                        options={tableOptions}
                    />
                }
            </Grid>
        </Grid>
    );
}

export default connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
    (state: ApplicationState) => state.vitalsContext,
    APIStore.actionCreators
)(DrillDownFacillity as any);
